import { Contact } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ControllerRenderProps, FieldValues } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { useIntl } from '@/core/i18n/use-intl';
import type { ContactValue } from '@/shared/schema';
import { Card, CardContent, CardHeader, CardTitle } from '../../card';
import { FormItem, FormMessage } from '../../form';
import { Input } from '../../input';
import { Label } from '../../label';
import AutoFormLabel from '../common/label';
import type { AutoFormInputComponentProps } from '../types';

interface ContactFieldsProps {
  value: Partial<ContactValue>;
  onFieldChange: (field: keyof Omit<ContactValue, 'fallbackContact'>, newValue: string) => void;
  disabled?: boolean;
  prefix?: string;
}

function ContactFields({ value, onFieldChange, disabled, prefix = '' }: ContactFieldsProps) {
  const { t } = useIntl();

  const [localName, setLocalName] = useState(value.name ?? '');
  const [localEmail, setLocalEmail] = useState(value.email ?? '');
  const [localPhone1, setLocalPhone1] = useState(value.phone1 ?? '');
  const [localPhone2, setLocalPhone2] = useState(value.phone2 ?? '');
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (value.name !== undefined && value.name !== localName) {
      setLocalName(value.name);
    }
    if (value.email !== undefined && value.email !== localEmail) {
      setLocalEmail(value.email);
    }
    if (value.phone1 !== undefined && value.phone1 !== localPhone1) {
      setLocalPhone1(value.phone1);
    }
    if (value.phone2 !== undefined && value.phone2 !== localPhone2) {
      setLocalPhone2(value.phone2);
    }
  }, [
    value.name,
    value.email,
    value.phone1,
    value.phone2,
    localName,
    localEmail,
    localPhone1,
    localPhone2,
  ]);

  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label htmlFor={`${prefix}name`}>{t('metadata.contact.name')}</Label>
        <Input
          id={`${prefix}name`}
          value={localName}
          onChange={(e) => {
            const newValue = e.target.value;
            setLocalName(newValue);
            onFieldChange('name', newValue);
          }}
          placeholder={t('metadata.contact.namePlaceholder')}
          disabled={disabled}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${prefix}email`}>{t('metadata.contact.email')}</Label>
        <Input
          id={`${prefix}email`}
          type="email"
          value={localEmail}
          onChange={(e) => {
            const newValue = e.target.value;
            setLocalEmail(newValue);
            onFieldChange('email', newValue);
          }}
          placeholder={t('metadata.contact.emailPlaceholder')}
          disabled={disabled}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${prefix}phone1`}>{t('metadata.contact.phone1')}</Label>
        <Input
          id={`${prefix}phone1`}
          type="tel"
          value={localPhone1}
          onChange={(e) => {
            const newValue = e.target.value;
            setLocalPhone1(newValue);
            onFieldChange('phone1', newValue);
          }}
          placeholder={t('metadata.contact.phonePlaceholder')}
          disabled={disabled}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${prefix}phone2`}>{t('metadata.contact.phone2')}</Label>
        <Input
          id={`${prefix}phone2`}
          type="tel"
          value={localPhone2}
          onChange={(e) => {
            const newValue = e.target.value;
            setLocalPhone2(newValue);
            onFieldChange('phone2', newValue);
          }}
          placeholder={t('metadata.contact.phonePlaceholder')}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

export default function AutoFormContact({
  label,
  isRequired,
  fieldConfigItem,
  field,
  fieldProps,
}: AutoFormInputComponentProps & {
  field: ControllerRenderProps<FieldValues, string>;
}) {
  const { t } = useIntl();
  const { setValue, getValues } = useFormContext();
  const { showLabel: _showLabel, disabled, ...restFieldProps } = fieldProps;
  const showLabel = _showLabel === undefined ? true : _showLabel;
  const fieldPath = field.name;

  const [contactValue, setContactValue] = useState<Partial<ContactValue>>(
    () => (getValues(fieldPath) as ContactValue) ?? {}
  );

  useEffect(() => {
    const currentFormValue = getValues(fieldPath) as ContactValue | undefined;
    if (currentFormValue && JSON.stringify(currentFormValue) !== JSON.stringify(contactValue)) {
      setContactValue(currentFormValue);
    }
  }, [fieldPath, getValues, contactValue]);

  const cleanContactValue = useCallback(
    (value: Partial<ContactValue>): Partial<ContactValue> | undefined => {
      const cleaned: Partial<ContactValue> = {};
      if (value.name) cleaned.name = value.name;
      if (value.email) cleaned.email = value.email;
      if (value.phone1) cleaned.phone1 = value.phone1;
      if (value.phone2) cleaned.phone2 = value.phone2;

      if (value.fallbackContact) {
        const cleanedFallback: Partial<Omit<ContactValue, 'fallbackContact'>> = {};
        if (value.fallbackContact.name) cleanedFallback.name = value.fallbackContact.name;
        if (value.fallbackContact.email) cleanedFallback.email = value.fallbackContact.email;
        if (value.fallbackContact.phone1) cleanedFallback.phone1 = value.fallbackContact.phone1;
        if (value.fallbackContact.phone2) cleanedFallback.phone2 = value.fallbackContact.phone2;

        if (Object.keys(cleanedFallback).length > 0) {
          cleaned.fallbackContact = cleanedFallback;
        }
      }

      return Object.keys(cleaned).length > 0 ? cleaned : undefined;
    },
    []
  );

  const handleMainFieldChange = useCallback(
    (fieldKey: keyof Omit<ContactValue, 'fallbackContact'>, newValue: string) => {
      setContactValue((prev) => {
        const updated = {
          ...prev,
          [fieldKey]: newValue || undefined,
        };
        const cleaned = cleanContactValue(updated);

        setValue(fieldPath, cleaned, {
          shouldDirty: true,
          shouldValidate: false,
        });

        return cleaned ?? {};
      });
    },
    [fieldPath, setValue, cleanContactValue]
  );

  const handleFallbackFieldChange = useCallback(
    (fieldKey: keyof Omit<ContactValue, 'fallbackContact'>, newValue: string) => {
      setContactValue((prev) => {
        const currentFallback = prev.fallbackContact ?? {};
        const updatedFallback = {
          ...currentFallback,
          [fieldKey]: newValue || undefined,
        };

        const updated = {
          ...prev,
          fallbackContact: updatedFallback,
        };
        const cleaned = cleanContactValue(updated);

        setValue(fieldPath, cleaned, {
          shouldDirty: true,
          shouldValidate: false,
        });

        return cleaned ?? {};
      });
    },
    [fieldPath, setValue, cleanContactValue]
  );

  return (
    <FormItem className="col-span-6" {...restFieldProps}>
      <Card className="border-border/60">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-base font-medium">
            <Contact className="size-4 text-muted-foreground" />
            {showLabel && (
              <AutoFormLabel
                label={fieldConfigItem?.label || label}
                isRequired={isRequired}
                className="text-base font-medium"
              />
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <ContactFields
            value={contactValue}
            onFieldChange={handleMainFieldChange}
            disabled={disabled}
            prefix="main-"
          />

          <div className="ml-6 pl-4 border-l-2 border-muted">
            <p className="text-sm font-medium mb-3">{t('metadata.contact.fallbackContact')}</p>
            <p className="text-sm text-muted-foreground mb-3">
              {t('metadata.contact.fallbackDescription')}
            </p>
            <ContactFields
              value={contactValue.fallbackContact ?? {}}
              onFieldChange={handleFallbackFieldChange}
              disabled={disabled}
              prefix="fallback-"
            />
          </div>

          <FormMessage />
        </CardContent>
      </Card>
    </FormItem>
  );
}
